import { Chip, Stack, Typography } from '@mui/material';
import type { ColumnDef } from '../common/tableFeatures';
import { StatusChip } from '../common/StatusChip';
import { clipped, dateSortValue, dateTime, selectColumn, shortDate } from '../common/tableCells';
import type { Device } from '../../types/api';

/** A device as the DPS export view sees it. `createdAt` is widened to optional
 *  because the export list tolerates devices the API left it off. */
export interface ExportDevice extends Omit<Device, 'createdAt'> {
  createdAt?: string;
  verifiedAt?: string;
}

/** TanStack column definitions for the Azure DPS export table. */
export const EXPORT_COLUMNS: ColumnDef<ExportDevice>[] = [
  selectColumn<ExportDevice>((row) => row.name, 'Select all devices'),
  {
    // The only column the page's global filter runs against; it inspects the
    // whole row, so letting every column opt in would repeat identical work.
    enableGlobalFilter: true,
    id: 'name',
    accessorFn: (row) => row.name,
    header: 'Device Name',
    cell: ({ row }) => (
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        {row.original.name}
      </Typography>
    ),
    size: 200,
  },
  {
    enableGlobalFilter: false,
    id: 'registrationId',
    accessorFn: (row) => row.registrationId || '',
    header: 'Registration ID',
    cell: ({ row }) => clipped(row.original.registrationId, 200, { mono: true }),
    size: 220,
  },
  {
    enableGlobalFilter: false,
    id: 'registrationStatus',
    header: 'Status',
    cell: ({ row }) => (
      <StatusChip status={row.original.registrationStatus || 'pending'} type="registration" />
    ),
    enableSorting: false,
    size: 130,
  },
  {
    enableGlobalFilter: false,
    id: 'tpm',
    header: 'TPM Info',
    cell: ({ row }) => {
      const { tpm } = row.original;
      if (!tpm) return '-';
      return (
        <Stack direction="column" spacing={0.5}>
          <Typography variant="caption">{tpm.manufacturer}</Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary'
          }}>
            v{tpm.version}
          </Typography>
        </Stack>
      );
    },
    enableSorting: false,
    size: 140,
  },
  {
    enableGlobalFilter: false,
    id: 'macAddress',
    header: 'MAC Address',
    cell: ({ row }) => clipped(row.original.macAddress, 150, { mono: true }),
    enableSorting: false,
    size: 160,
  },
  {
    enableGlobalFilter: false,
    id: 'createdAt',
    accessorFn: (row) => dateSortValue(row.createdAt),
    header: 'Created',
    cell: ({ row }) => (
      <Typography variant="body2" sx={{
        color: 'text.secondary'
      }}>
        {shortDate(row.original.createdAt)}
      </Typography>
    ),
    size: 120,
  },
  {
    enableGlobalFilter: false,
    id: 'verifiedAt',
    accessorFn: (row) => dateSortValue(row.verifiedAt),
    header: 'Verified',
    cell: ({ row }) =>
      row.original.verifiedAt ? (
        <Chip
          label={shortDate(row.original.verifiedAt)}
          color="success"
          size="small"
          variant="outlined"
        />
      ) : (
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          -
        </Typography>
      ),
    size: 130,
  },
  {
    enableGlobalFilter: false,
    id: 'lastSeen',
    accessorFn: (row) => dateSortValue(row.lastSeen),
    header: 'Last Seen',
    cell: ({ row }) => (
      <Typography variant="body2" sx={{
        color: 'text.secondary'
      }}>
        {dateTime(row.original.lastSeen)}
      </Typography>
    ),
    size: 170,
  },
];
